import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, IconButton, Typography, Paper, Tooltip
} from '@mui/material';
import { keyframes } from '@mui/system';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import { BandRecommendation } from '../../types/BandRecommendations';
import { getPlaylistFromBands, PlaylistItem } from '../../utils/youtubeUtils';
import { trackEvent } from '../../utils/analytics';

const bounce = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50%       { transform: scaleY(1); }
`;

const watchPulseAnim = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(1); }
  50%       { opacity: 1;    transform: scale(1.06); }
`;

const BARS = [
  { delay: '0s',    duration: '0.7s' },
  { delay: '0.15s', duration: '0.9s' },
  { delay: '0.05s', duration: '0.6s' },
  { delay: '0.25s', duration: '1.0s' },
  { delay: '0.1s',  duration: '0.75s' },
];

const EqualizerBars: React.FC<{ active: boolean }> = ({ active }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 20, flexShrink: 0 }}>
    {BARS.map((bar, i) => (
      <Box
        key={i}
        sx={{
          width: 3,
          height: '100%',
          bgcolor: '#50E3C2',
          borderRadius: '2px 2px 0 0',
          transformOrigin: 'bottom',
          transform: active ? undefined : 'scaleY(0.3)',
          animation: active
            ? `${bounce} ${bar.duration} ${bar.delay} ease-in-out infinite`
            : 'none',
          transition: 'transform 0.3s ease',
          opacity: active ? 1 : 0.35,
        }}
      />
    ))}
  </Box>
);

interface Props {
  bands: BandRecommendation[];
  playNowId?: string | null;
  onStateChange?: (bandId: string | null, isPlaying: boolean) => void;
}

const loadYouTubeAPI = (): Promise<void> =>
  new Promise(resolve => {
    if (window.YT && window.YT.Player) { resolve(); return; }
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    // Preserve any existing handler
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { resolve(); if (prev) prev(); };
    const first = document.getElementsByTagName('script')[0];
    first.parentNode?.insertBefore(tag, first);
  });

const PublicAudioPlayer: React.FC<Props> = ({ bands, playNowId, onStateChange }) => {
  const playerRef = useRef<YT.Player | null>(null);
  const trackedPlaysRef = useRef<Set<string>>(new Set());
  const watchPulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [playlist, setPlaylist] = useState<PlaylistItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [watchPulse, setWatchPulse] = useState(false);

  const currentItem = playlist[currentIndex] ?? null;

  const initPlayer = useCallback((videoId: string) => {
    try {
      playerRef.current?.destroy();
      playerRef.current = new window.YT.Player('public-audio-player', {
        videoId,
        width: '100%',
        height: 360,
        playerVars: { playsinline: 1, autoplay: 0, controls: 0, rel: 0, modestbranding: 1 },
        events: {
          onReady: () => setIsReady(true),
          onStateChange: (e: YT.OnStateChangeEvent) => {
            const playing = e.data === window.YT.PlayerState.PLAYING;
            setIsPlaying(playing);
            if (onStateChange) {
              const item = playlist[currentIndex] ?? null;
              onStateChange(item?.bandId ?? null, playing);
            }
            // Auto-advance when track ends
            if (e.data === window.YT.PlayerState.ENDED) {
              setCurrentIndex(prev => {
                const next = (prev + 1) % playlist.length;
                setTimeout(() => {
                  const nextItem = playlist[next];
                  if (nextItem) playerRef.current?.loadVideoById(nextItem.videoId);
                }, 0);
                return next;
              });
            }
          },
          onError: () => {},
        },
      });
    } catch {}
  }, [playlist]);

  useEffect(() => {
    const list = getPlaylistFromBands(bands);
    setPlaylist(list);
    setCurrentIndex(0);
    setIsReady(false);
    setIsPlaying(false);
    if (!list.length) return;
    loadYouTubeAPI().then(() => setTimeout(() => initPlayer(list[0].videoId), 200));
  }, [bands]);

  useEffect(() => () => { try { playerRef.current?.destroy(); } catch {} }, []);

  // Pulse the Watch button for 6s when playback starts (not if video already open)
  useEffect(() => {
    if (watchPulseTimerRef.current) clearTimeout(watchPulseTimerRef.current);
    if (isPlaying && !videoExpanded) {
      setWatchPulse(true);
      watchPulseTimerRef.current = setTimeout(() => setWatchPulse(false), 6000);
    } else {
      setWatchPulse(false);
    }
    return () => {
      if (watchPulseTimerRef.current) clearTimeout(watchPulseTimerRef.current);
    };
  }, [isPlaying, videoExpanded]);

  // Track first play per band — fires once per band per session
  useEffect(() => {
    if (!isPlaying) return;
    const item = playlist[currentIndex];
    if (!item?.bandId || trackedPlaysRef.current.has(item.bandId)) return;
    trackedPlaysRef.current.add(item.bandId);
    trackEvent('audio_played', { bandId: item.bandId, bandName: item.bandName });
  }, [isPlaying, currentIndex]);

  // Jump to band or toggle pause if already playing that band
  useEffect(() => {
    if (!playNowId || !playlist.length || !playerRef.current) return;
    const bandId = playNowId.split('_')[0];
    const idx = playlist.findIndex(item => item.bandId === bandId);
    if (idx === -1) return;
    if (idx === currentIndex) {
      // Ask the player directly — avoids stale closure on isPlaying
      const state = playerRef.current.getPlayerState();
      if (state === window.YT.PlayerState.PLAYING) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    } else {
      navigateTo(idx);
    }
  }, [playNowId]);

  const navigateTo = (index: number) => {
    const item = playlist[index];
    if (!item || !playerRef.current) return;
    playerRef.current.loadVideoById(item.videoId);
    setCurrentIndex(index);
  };

  const goNext = () => navigateTo((currentIndex + 1) % playlist.length);
  const goPrev = () => navigateTo(currentIndex === 0 ? playlist.length - 1 : currentIndex - 1);

  const togglePlay = () => {
    if (!playerRef.current || !isReady) return;
    if (isPlaying) playerRef.current.pauseVideo();
    else playerRef.current.playVideo();
  };

  if (!playlist.length) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        mb: 4,
        borderRadius: 3,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #0d1b3e 60%, #0a2a1a 100%)',
      }}
    >
      {/* Hidden video area — audio keeps playing while clipped */}
      <Box
        sx={{
          height: videoExpanded ? { xs: '56.25vw', md: 360 } : 0,
          overflow: 'hidden',
          transition: 'height 0.4s ease',
          bgcolor: 'black',
          '& iframe': { width: '100% !important', height: '100% !important' },
        }}
      >
        <div id="public-audio-player" style={{ width: '100%', height: '100%' }} />
      </Box>

      {/* Player bar */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, md: 3 },
        }}
      >
        {/* Track info — left */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {isPlaying
              ? <EqualizerBars active={true} />
              : <MusicNoteIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
            }
            <Typography
              variant="caption"
              sx={{
                color: isPlaying ? '#50E3C2' : 'rgba(255,255,255,0.4)',
                letterSpacing: '0.1em',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                transition: 'color 0.3s ease',
              }}
            >
              {!isReady ? 'Loading…' : isPlaying ? 'Now Playing' : 'Irish Jazz'}
            </Typography>
          </Box>
          <Typography
            variant="h6"
            fontWeight={700}
            noWrap
            sx={{ color: 'white', fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.2 }}
          >
            {currentItem?.bandName ?? '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            {currentIndex + 1} of {playlist.length} {bands.length === 1 ? 'tracks' : 'artists'}
          </Typography>
        </Box>

        {/* Controls — centre */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexShrink: 0 }}>
          <IconButton
            onClick={goPrev}
            disabled={!isReady || playlist.length <= 1}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
          >
            <SkipPreviousIcon sx={{ fontSize: 28 }} />
          </IconButton>

          {/* Large play button */}
          <IconButton
            onClick={togglePlay}
            disabled={!isReady}
            sx={{
              width: 64,
              height: 64,
              bgcolor: isReady ? '#50E3C2' : 'rgba(255,255,255,0.1)',
              color: '#0a0a0f',
              boxShadow: isPlaying ? '0 0 24px rgba(80,227,194,0.5)' : '0 4px 16px rgba(0,0,0,0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: '#7EECD4',
                transform: 'scale(1.07)',
                boxShadow: '0 0 32px rgba(80,227,194,0.6)',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.2)',
                boxShadow: 'none',
              },
            }}
          >
            {isPlaying
              ? <PauseIcon sx={{ fontSize: 32 }} />
              : <PlayArrowIcon sx={{ fontSize: 32 }} />
            }
          </IconButton>

          <IconButton
            onClick={goNext}
            disabled={!isReady || playlist.length <= 1}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}
          >
            <SkipNextIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>

        {/* Video toggle — right */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title={videoExpanded ? 'Hide video' : 'Watch video'}>
            <IconButton
              onClick={() => setVideoExpanded(v => !v)}
              sx={{
                color: videoExpanded ? '#50E3C2' : watchPulse ? '#50E3C2' : 'rgba(255,255,255,0.4)',
                border: '1px solid',
                borderColor: videoExpanded ? '#50E3C2' : watchPulse ? '#50E3C2' : 'rgba(255,255,255,0.15)',
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                gap: 0.75,
                transition: 'color 0.25s ease, border-color 0.25s ease',
                animation: watchPulse ? `${watchPulseAnim} 1.3s ease-in-out infinite` : 'none',
                '&:hover': { color: 'white', borderColor: 'rgba(255,255,255,0.5)' },
              }}
            >
              {videoExpanded ? <VideocamOffIcon sx={{ fontSize: 18 }} /> : <VideocamIcon sx={{ fontSize: 18 }} />}
              <Typography variant="caption" sx={{ color: 'inherit', fontSize: '0.7rem', display: { xs: 'none', sm: 'block' } }}>
                {videoExpanded ? 'Hide' : 'Watch'}
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default PublicAudioPlayer;
